import React from 'react';

// Allergen definitions with icons and translations
const allergenData = {
  gluten: {
    icon: '🌾',
    name: { es: 'Gluten', en: 'Gluten', cat: 'Gluten' },
    color: '#D4A574'
  },
  milk: {
    icon: '🥛',
    name: { es: 'Lácteos', en: 'Milk', cat: 'Lactis' },
    color: '#87CEEB'
  },
  egg: {
    icon: '🥚',
    name: { es: 'Huevo', en: 'Egg', cat: 'Ou' },
    color: '#FFE4B5'
  },
  fish: {
    icon: '🐟',
    name: { es: 'Pescado', en: 'Fish', cat: 'Peix' },
    color: '#4682B4'
  },
  nuts: {
    icon: '🥜',
    name: { es: 'Frutos secos', en: 'Nuts', cat: 'Fruits secs' },
    color: '#8B4513'
  },
  mustard: {
    icon: '🟡',
    name: { es: 'Mostaza', en: 'Mustard', cat: 'Mostassa' },
    color: '#FFD700'
  },
  soy: {
    icon: '🫘',
    name: { es: 'Soja', en: 'Soy', cat: 'Soja' },
    color: '#90EE90'
  },
  sulfites: {
    icon: '🍷',
    name: { es: 'Sulfitos', en: 'Sulfites', cat: 'Sulfits' },
    color: '#722F37'
  },
  molluscs: {
    icon: '🦪',
    name: { es: 'Moluscos', en: 'Molluscs', cat: 'Mol·luscs' },
    color: '#708090'
  },
  celery: {
    icon: '🥬',
    name: { es: 'Apio', en: 'Celery', cat: 'Api' },
    color: '#32CD32'
  },
  crustaceans: {
    icon: '🦐',
    name: { es: 'Crustáceos', en: 'Crustaceans', cat: 'Crustacis' },
    color: '#FF6347'
  },
  sesame: {
    icon: '⚪',
    name: { es: 'Sésamo', en: 'Sesame', cat: 'Sèsam' },
    color: '#F5DEB3'
  },
  lupin: {
    icon: '🌸',
    name: { es: 'Altramuces', en: 'Lupin', cat: 'Tramussos' },
    color: '#DDA0DD'
  },
  peanuts: {
    icon: '🥜',
    name: { es: 'Cacahuetes', en: 'Peanuts', cat: 'Cacauets' },
    color: '#CD853F'
  }
};

// Single allergen badge component
const AllergenBadge = ({ allergen, language = 'es', size = 'sm', showLabel = false }) => {
  const data = allergenData[allergen];
  if (!data) return null;

  const sizeClasses = {
    xs: 'w-5 h-5 text-xs',
    sm: 'w-6 h-6 text-sm',
    md: 'w-8 h-8 text-base',
    lg: 'w-10 h-10 text-lg'
  };

  return (
    <div 
      className="flex items-center gap-1"
      title={data.name[language] || data.name.es}
    >
      <span 
        className={`${sizeClasses[size]} flex items-center justify-center rounded-full flex-shrink-0`}
        style={{ 
          backgroundColor: `${data.color}20`,
          border: `1px solid ${data.color}40`
        }}
      >
        {data.icon}
      </span>
      {showLabel && (
        <span className="text-xs text-stone-600">
          {data.name[language] || data.name.es}
        </span>
      )}
    </div>
  );
};

// Allergen icons row component (for popup)
export const AllergenIconsRow = ({ 
  allergens = [], 
  language = 'es', 
  size = 'sm',
  showLabels = false,
  maxVisible = 10 
}) => {
  if (!allergens || allergens.length === 0) return null;

  const visibleAllergens = allergens.slice(0, maxVisible);
  const remaining = allergens.length - maxVisible;

  return (
    <div className="flex flex-wrap gap-1.5 items-center">
      {visibleAllergens.map((allergen) => (
        <AllergenBadge 
          key={allergen} 
          allergen={allergen} 
          language={language}
          size={size}
          showLabel={showLabels}
        />
      ))}
      {remaining > 0 && (
        <span className="text-xs text-stone-500">+{remaining}</span>
      )}
    </div>
  );
};

// Full allergen section for product popup
export const AllergenSection = ({ allergens = [], language = 'es' }) => {
  const noAllergensText = {
    es: 'Sin alérgenos declarables',
    en: 'No declarable allergens',
    cat: 'Sense al·lèrgens declarables'
  };

  const sectionTitle = {
    es: 'Alérgenos',
    en: 'Allergens',
    cat: 'Al·lèrgens'
  };

  return (
    <div className="py-3">
      <p 
        className="text-xs font-medium uppercase tracking-wider mb-2"
        style={{ color: 'var(--stone-500)' }}
      >
        {sectionTitle[language] || sectionTitle.es}
      </p>
      
      {(!allergens || allergens.length === 0) ? (
        <p className="text-sm italic" style={{ color: '#9A958C' }}>
          {noAllergensText[language] || noAllergensText.es}
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {allergens.map((allergen) => {
            const data = allergenData[allergen];
            if (!data) return null;
            
            return (
              <div 
                key={allergen}
                className="flex items-center gap-1.5 px-2 py-1 rounded-full"
                style={{ 
                  backgroundColor: `${data.color}15`,
                  border: `1px solid ${data.color}30`
                }}
              >
                <span className="text-sm">{data.icon}</span>
                <span 
                  className="text-xs font-medium"
                  style={{ color: 'var(--stone-600)' }}
                >
                  {data.name[language] || data.name.es}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Compact allergen icons for product cards (list view)
export const AllergenIconsCompact = ({ allergens = [], language = 'es', maxShow = 4 }) => {
  if (!allergens || allergens.length === 0) return null;

  const visibleAllergens = allergens.slice(0, maxShow);
  const remaining = allergens.length - maxShow;

  return (
    <div className="flex items-center gap-0.5">
      {visibleAllergens.map((allergen) => {
        const data = allergenData[allergen];
        if (!data) return null;
        
        return (
          <span 
            key={allergen}
            className="text-xs"
            title={data.name[language] || data.name.es}
          >
            {data.icon}
          </span>
        );
      })}
      {remaining > 0 && (
        <span className="text-[10px] text-stone-400 ml-0.5">+{remaining}</span>
      )}
    </div>
  );
};

// Export allergen data for admin panel
export const getAllergenOptions = () => {
  return Object.entries(allergenData).map(([key, value]) => ({
    value: key,
    label: value.name,
    icon: value.icon
  }));
};

export default AllergenSection;
